import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { ArticleRow, CountryRow, CustomerRow, PeriodQuery, SalesReport } from "../../api/reports";
import { reportsApi } from "../../api/reports";
import { formatNumber } from "../../lib/format";
import type { useChartPalette } from "../../theme/useChartPalette";
import { StabloRegistar } from "../../components/reports/StabloRegistar";
import type { StabloRed, StabloSort } from "../../components/reports/StabloRegistar";
import { useToast } from "../../components/Toast";

type Props = {
  podaci: SalesReport;
  upit: PeriodQuery;
  locale: string;
  palette: ReturnType<typeof useChartPalette>;
  ucitava: boolean;
};

/**
 * Prodaja po zemljama, sa spuštanjem do kupca i do artikla.
 *
 * <p>Zatečena tabela je stajala na jednom nivou: čitalac je video da je Holandija pala 46 % i
 * nije imao kuda dalje. Spuštanje odgovara na pitanje koje svaki pad otvara — KOD KOGA je pao —
 * a opseg putuje sa njim: spisak kupaca otvoren iz Srbije nosi samo srpske kupce, i to je
 * provereno na serveru (`SalesDrillIT`), ne pretpostavljeno.
 *
 * <p>Zemlja nosi DVA udela, jedina u izveštaju: udeo u marži stoji uz traku (sa prošlogodišnjim
 * pored njega), a udeo količine kao sitan red ispod količine. Original ih traži oba, za obe
 * godine, i nijedan nije sklonjen. Niži nivoi te udele ne dobijaju — server ih ne računa, a
 * izmišljen udeo je gori od nijednog.
 */
export default function ZemljeTab({ podaci, upit, locale, palette, ucitava }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const [otvorene, setOtvorene] = useState<Set<string>>(new Set());
  const [kupci, setKupci] = useState<Record<string, CustomerRow[]>>({});
  const [artikli, setArtikli] = useState<Record<string, ArticleRow[]>>({});
  const [dovlaci, setDovlaci] = useState<string | null>(null);
  const [sort, setSort] = useState<StabloSort>(null);
  const [opadajuce, setOpadajuce] = useState(true);

  const najvece = useMemo(() => podaci.countries.slice(0, 15), [podaci.countries]);
  const ukupanRuc = podaci.totals.ruc.current;
  /**
   * Da li se iz ovog perioda sme čitati rast ili pad.
   *
   * Netačno kad period prelazi presek izvora: septembar 2026 nosi 22 dana a poredi se sa punim
   * mesecom. Oba broja ostaju na ekranu, smer se ne piše.
   */
  const tumaci = podaci.period.coverage == null || podaci.period.coverage.current === "FULL";

  // Pareto: koliko zemalja nosi koliki deo RUC-a. Odgovara na pitanje „gde je posao", koje
  // spisak od 46 redova ne odgovara.
  const pareto = useMemo(() => {
    const pozitivne = podaci.countries.filter((z) => z.ruc.current > 0);
    const ukupno = pozitivne.reduce((a, z) => a + z.ruc.current, 0);
    let zbir = 0;
    return pozitivne.map((z, i) => {
      zbir += z.ruc.current;
      return {
        ime: z.name,
        rang: i + 1,
        kumulativ: ukupno > 0 ? Number(((100 * zbir) / ukupno).toFixed(1)) : 0,
      };
    });
  }, [podaci.countries]);

  const sortiraj = useCallback(
    (k: StabloSort) => {
      if (k === sort) setOpadajuce((o) => !o);
      else {
        setSort(k);
        setOpadajuce(k !== "ime");
      }
    },
    [sort],
  );

  const otvori = useCallback(
    async (kljuc: string) => {
      const bio = otvorene.has(kljuc);
      setOtvorene((pre) => {
        const sled = new Set(pre);
        if (bio) sled.delete(kljuc);
        else sled.add(kljuc);
        return sled;
      });
      if (bio) return;

      // Ključ je putanja kroz nivoe: „SRBIJA", „SRBIJA\u0000K1", „SRBIJA\u0000K1\u0000A1".
      // Razdvojnik je znak koji ime zemlje ni šifra kupca ne mogu da nose.
      const deo = kljuc.split("\u0000");
      if (deo.length === 1) {
        if (kupci[kljuc]) return;
        setDovlaci(kljuc);
        try {
          const o = await reportsApi.salesCustomers(upit, deo[0]);
          // Spor odgovor ne sme da sleti pod zemlju koju je čovek u međuvremenu otvorio.
          if ((o.country ?? "") === deo[0]) setKupci((pre) => ({ ...pre, [kljuc]: o.customers }));
        } catch (e) {
          toast.error((e as Error).message);
          setOtvorene((pre) => {
            const sled = new Set(pre);
            sled.delete(kljuc);
            return sled;
          });
        } finally {
          setDovlaci((k) => (k === kljuc ? null : k));
        }
        return;
      }

      if (deo.length === 2) {
        if (artikli[kljuc]) return;
        setDovlaci(kljuc);
        try {
          const o = await reportsApi.salesCustomerArticles(upit, deo[1], deo[0]);
          if (o.erpCustomerId === deo[1] && (o.country ?? "") === deo[0]) {
            setArtikli((pre) => ({ ...pre, [kljuc]: o.articles }));
          }
        } catch (e) {
          toast.error((e as Error).message);
          setOtvorene((pre) => {
            const sled = new Set(pre);
            sled.delete(kljuc);
            return sled;
          });
        } finally {
          setDovlaci((k) => (k === kljuc ? null : k));
        }
      }
    },
    [artikli, kupci, otvorene, toast, upit],
  );

  const stablo: StabloRed[] = useMemo(
    () =>
      poredjaj(podaci.countries, sort, opadajuce).map((z) => {
        const kljucZ = z.name;
        const uKupce = kupci[kljucZ];
        return {
          kljuc: kljucZ,
          ime: z.name || "/",
          // Udeo u marži nosi traka, a prošlogodišnji stoji pored nje — jedan broj, dva perioda.
          udeo: z.shareRuc,
          udeoPre: z.prevShareRuc,
          mere: [
            { tumaci, sada: z.ruc.current, pre: z.ruc.previous },
            {
              sada: z.quantity.current,
              pre: z.quantity.previous,
              jedinica: "kg",
              decimala: 0,
              // Udeo količine je DRUGI broj od udela marže i ovde mu je jedino mesto.
              udeo: z.shareQuantity,
              udeoPre: z.prevShareQuantity,
            },
            { tumaci, sada: z.salesValue.current, pre: z.salesValue.previous },
          ],
          mozeDaSeOtvori: true,
          prazno: t("report.customers.empty", "Za ovaj opseg nema kupaca u periodu."),
          deca: uKupce?.map((k) => {
            const kljucK = `${kljucZ}\u0000${k.erpId}`;
            const uArtikle = artikli[kljucK];
            return {
              kljuc: kljucK,
              ime: k.name || k.erpId,
              udeo: udeo(k.ruc.current, ukupanRuc),
              mere: [
                { tumaci, sada: k.ruc.current, pre: k.ruc.previous },
                { tumaci, sada: k.quantity.current, pre: k.quantity.previous, jedinica: "kg", decimala: 0 },
                { tumaci, sada: k.salesValue.current, pre: k.salesValue.previous },
              ],
              mozeDaSeOtvori: true,
              prazno: t("report.articles.empty", "Za ovu grupu nema artikala u periodu."),
              deca: uArtikle?.map((a) => ({
                kljuc: `${kljucK}\u0000${a.code}`,
                ime: a.name,
                oznaka: a.code,
                udeo: udeo(a.ruc.current, ukupanRuc),
                mere: [
                  { tumaci, sada: a.ruc.current, pre: a.ruc.previous },
                  { tumaci, sada: a.quantity.current, pre: a.quantity.previous, jedinica: "kg", decimala: 0 },
                  { tumaci, sada: a.salesValue.current, pre: a.salesValue.previous },
                ],
              })),
            };
          }),
        };
      }),
    [artikli, kupci, opadajuce, podaci.countries, sort, t, ukupanRuc],
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, opacity: ucitava ? 0.55 : 1 }}>
      <section className="card" style={{ padding: 14 }}>
        <h3 style={{ margin: "0 0 4px", fontSize: 15 }}>{t("report.chart.topCountries", "Zemlje po RUC-u")}</h3>
        <div className="sub" style={{ marginBottom: 10 }}>
          {t("report.chart.topCountriesHint", "Petnaest najvećih; ostatak je u tabeli ispod.")}
        </div>
        <ResponsiveContainer width="100%" height={Math.max(260, najvece.length * 26)}>
          <BarChart data={najvece} layout="vertical" margin={{ top: 4, right: 12, bottom: 4, left: 8 }}>
            <CartesianGrid stroke={palette.grid} strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11, fill: palette.ink }} axisLine={false} tickLine={false} />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fontSize: 11, fill: palette.ink }}
              axisLine={false}
              tickLine={false}
              width={150}
            />
            <Tooltip
              contentStyle={{ background: palette.surface, border: `1px solid ${palette.border}`, borderRadius: 8 }}
              labelStyle={{ color: palette.inkStrong }}
              formatter={(value, name) => [formatNumber(Number(value), locale), String(name)]}
            />
            <Bar dataKey="ruc.current" name={t("report.col.ruc", "RUC")} radius={[0, 3, 3, 0]}>
              {najvece.map((z) => (
                <Cell key={z.name} fill={z.ruc.current < 0 ? "var(--warn)" : palette.brand} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </section>

      {pareto.length > 2 && (
        <section className="card" style={{ padding: 14 }}>
          <h3 style={{ margin: "0 0 4px", fontSize: 15 }}>
            {t("report.chart.pareto", "Koliko zemalja nosi koliki deo RUC-a")}
          </h3>
          <div className="sub" style={{ marginBottom: 10 }}>
            {t("report.chart.paretoHint", "Zemlje su poređane od najveće; kriva je kumulativni udeo.")}
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart data={pareto} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid stroke={palette.grid} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="rang" tick={{ fontSize: 11, fill: palette.ink }} axisLine={false} tickLine={false} />
              <YAxis
                domain={[0, 100]}
                tick={{ fontSize: 11, fill: palette.ink }}
                axisLine={false}
                tickLine={false}
                width={44}
                unit="%"
              />
              <Tooltip
                contentStyle={{ background: palette.surface, border: `1px solid ${palette.border}`, borderRadius: 8 }}
                labelFormatter={(v) => t("report.chart.rank", { broj: v, defaultValue: "{{broj}}. zemlja" })}
                formatter={(value, _name, item) => [
                  `${Number(value)} %`,
                  String((item?.payload as { ime?: string } | undefined)?.ime ?? ""),
                ]}
              />
              <Line dataKey="kumulativ" stroke={palette.brand} strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </section>
      )}

      <section className="card" style={{ padding: 0 }}>
        {podaci.countries.length === 0 ? (
          <div className="empty" style={{ padding: 18 }}>
            {t("report.emptyCountries", "Za izabrani period nema prodaje po zemljama.")}
          </div>
        ) : (
          <StabloRegistar
            kolone={[
              t("report.col.country", "Zemlja"),
              t("report.col.ruc", "RUC"),
              t("report.col.quantity", "Količina"),
              t("report.col.value", "Vrednost"),
            ]}
            naslovUdela={t("report.col.shareRuc", "Udeo RUC")}
            // Udeo zemlje se poredi sa štampanim izveštajem, gde stoji na dve decimale.
            decimalaUdela={2}
            sort={sort}
            opadajuce={opadajuce}
            naSortiranje={sortiraj}
            redovi={stablo}
            otvoreni={otvorene}
            naOtvaranje={otvori}
            ucitava={dovlaci}
            zbir={{
              kljuc: "__ukupno",
              ime: t("report.total", "Ukupno"),
              udeo: ukupanRuc === 0 ? null : 100,
              mere: [
                { tumaci, sada: podaci.totals.ruc.current, pre: podaci.totals.ruc.previous },
                {
                  sada: podaci.totals.quantity.current,
                  pre: podaci.totals.quantity.previous,
                  jedinica: "kg",
                  decimala: 0,
                  udeo: ukupanRuc === 0 ? null : 100,
                },
                { tumaci, sada: podaci.totals.salesValue.current, pre: podaci.totals.salesValue.previous },
              ],
            }}
            locale={locale}
          />
        )}
      </section>
    </div>
  );
}

/** Udeo u ukupnoj marži; bez ukupnog nema udela, a nula nije odgovor. */
function udeo(deo: number, ukupno: number): number | null {
  if (!ukupno) return null;
  return (deo / ukupno) * 100;
}

/**
 * Ređa zemlje po izabranoj koloni.
 *
 * <p>Vraća NOV niz: ređanje na licu mesta bi menjalo niz koji je došao od pozivaoca, pa se
 * zatečeni redosled sa servera — redosled štampanog izveštaja — ne bi mogao vratiti.
 */
function poredjaj(zemlje: CountryRow[], sort: StabloSort, opadajuce: boolean): CountryRow[] {
  if (sort === null) return zemlje;
  const znak = opadajuce ? -1 : 1;
  if (sort === "ime") return [...zemlje].sort((a, b) => znak * a.name.localeCompare(b.name));
  // Redosled mera je redosled kolona: 0 RUC, 1 količina, 2 vrednost.
  const mera = (z: CountryRow) =>
    sort === 0 ? z.ruc.current : sort === 1 ? z.quantity.current : z.salesValue.current;
  return [...zemlje].sort((a, b) => znak * (mera(a) - mera(b)));
}
